import { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ParticipationModal from '../ParticipationModal';
import { labelTableTopicsPages } from '_helpers/tableTopics';

const FIELDS = [
  { key: 'commend', label: 'What Went Well', placeholder: 'What worked well?' },
  { key: 'recommend', label: 'Recommend', placeholder: 'What could be improved, and how?' },
  { key: 'overall', label: 'Overall', placeholder: 'Overall impression' },
];
const MAX_LENGTH = 2000;

const toDraft = (entries = []) => Object.fromEntries(
  (Array.isArray(entries) ? entries : []).map(entry => [String(entry.agendaItemId), entry])
);

export default function TableTopicsReviewForm({ embedded, show, onHide, tableTopics, items }) {
  const title = 'Table Topics Evaluation';
  const pages = useMemo(() => labelTableTopicsPages(tableTopics.pages, items), [tableTopics.pages, items]);
  const [pageIndex, setPageIndex] = useState(0);
  const [draft, setDraft] = useState({});

  // Start from the saved review each time the form opens; live reloads must not wipe typing.
  useEffect(() => {
    if (!show) return;
    setDraft(toDraft(tableTopics.mine?.entries));
    setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const page = pages[Math.min(pageIndex, Math.max(pages.length - 1, 0))];
  const value = page ? draft[page.agendaItemId] || {} : {};
  const savedPage = tableTopics.mine?.entries?.find(entry => String(entry.agendaItemId) === String(page?.agendaItemId));
  const isPublished = Boolean(savedPage?.publishedAt);
  const hasUnpublishedChanges = isPublished && FIELDS.some(field => (
    String(value[field.key] || '') !== String(savedPage?.publishedContent?.[field.key] || '')
  ));
  const setField = (field, text) => setDraft(current => ({
    ...current,
    [page.agendaItemId]: { ...current[page.agendaItemId], agendaItemId: page.agendaItemId, [field]: text },
  }));
  const isFilled = item => FIELDS.some(field => String(draft[item.agendaItemId]?.[field.key] || '').trim());

  const handleSave = async () => {
    try {
      await tableTopics.save(pages.map(item => draft[item.agendaItemId]).filter(Boolean));
    } catch (error) {
      toast.error(`Unable to save: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handlePublish = async (agendaItemIds = null) => {
    try {
      await tableTopics.save(pages.map(item => draft[item.agendaItemId]).filter(Boolean));
      await tableTopics.publish(agendaItemIds ? { agendaItemIds } : { publishAll: true });
    } catch (error) {
      toast.error(`Unable to publish: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const footer = (
    <>
      <Button variant="outline-secondary" onClick={() => setPageIndex(index => index - 1)} disabled={pageIndex === 0} aria-label="Previous page">
        <i className="bi bi-arrow-left"></i>
      </Button>
      <Button variant="outline-secondary" onClick={() => setPageIndex(index => index + 1)} disabled={pageIndex >= pages.length - 1} aria-label="Next page">
        <i className="bi bi-arrow-right"></i>
      </Button>
      <Button
        variant="outline-primary"
        onClick={() => handlePublish()}
        disabled={tableTopics.saving || !tableTopics.active || !pages.length || !Object.values(draft).some(entry => FIELDS.some(field => String(entry?.[field.key] || '').trim()))}
      >
        Publish all
      </Button>
      <Button variant="success" className="ms-auto" onClick={handleSave} disabled={tableTopics.saving || !tableTopics.active || !pages.length}>
        {tableTopics.saving ? 'Saving...' : 'Save evaluation'}
      </Button>
    </>
  );

  return (
    <ParticipationModal embedded={embedded} show={show} onHide={onHide} title={title} icon="bi-chat-quote-fill" footer={footer}>
      {!page ? (
        <p className="mb-0">There are no Table Topics items on this agenda.</p>
      ) : (
        <div className="table-topics-review text-start">
          <div className="table-topics-review-pages d-flex flex-wrap justify-content-center gap-1 mb-3" role="tablist" aria-label="Table Topics pages">
            {pages.map((item, index) => (
              <Button
                key={item.agendaItemId}
                size="sm"
                role="tab"
                aria-selected={index === pageIndex}
                aria-label={`${item.title} - ${item.person}`}
                variant={index === pageIndex ? 'primary' : isFilled(item) ? 'outline-success' : 'outline-secondary'}
                onClick={() => setPageIndex(index)}
              >
                {item.kind === 'master' ? 'TTM' : item.title.replace(/^Speaker\s*/, '')}
              </Button>
            ))}
          </div>
          <div className="text-center mb-3">
            <div className="small" style={{ opacity: 0.85 }}>Page {pageIndex + 1} of {pages.length}</div>
            <h5 className="mb-0">{page.title} - {page.person}</h5>
            {page.topic && <div className="small fst-italic">{page.topic}</div>}
            {isPublished && !hasUnpublishedChanges && <div className="small text-success mt-1">Published</div>}
            {hasUnpublishedChanges && <div className="small text-warning mt-1">Unpublished changes</div>}
          </div>
          {FIELDS.map(field => (
            <Form.Group key={field.key} className="mb-3" controlId={`tt-${page.agendaItemId}-${field.key}`}>
              <Form.Label className="mb-1">{field.label}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={MAX_LENGTH}
                placeholder={field.placeholder}
                value={value[field.key] || ''}
                onChange={event => setField(field.key, event.target.value)}
              />
            </Form.Group>
          ))}
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant={isPublished && !hasUnpublishedChanges ? 'outline-success' : 'primary'}
              onClick={() => handlePublish([page.agendaItemId])}
              disabled={tableTopics.saving || !tableTopics.active || !isFilled(page)}
            >
              {tableTopics.saving ? 'Publishing...' : isPublished ? 'Republish this page' : 'Publish this page'}
            </Button>
          </div>
        </div>
      )}
    </ParticipationModal>
  );
}
